import { Globe, MoreVertical } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApplicationLanguage } from "../../../constants";
import { setCookie } from "../../../utils/cookieHandlers";
import { Heading, Text } from "../../../components/elements";
import HubertLogo from "../../../assets/images/hubert-logo.png";
import "./OutreachShared.css";

const availableUiLanguages = [
    { value: ApplicationLanguage.EN_EU, label: "English" },
    { value: ApplicationLanguage.SV_SE, label: "Svenska" },
    { value: ApplicationLanguage.DA_DK, label: "Dansk" },
    { value: ApplicationLanguage.DE_DE, label: "Deutsch" },
    { value: ApplicationLanguage.NL_NL, label: "Nederlands" },
    { value: ApplicationLanguage.PT_PT, label: "Português" },
    { value: ApplicationLanguage.TR_TR, label: "Türkçe" },
    { value: ApplicationLanguage.NO_NO, label: "Norsk" },
    { value: ApplicationLanguage.FI_FI, label: "Suomi" },
    { value: ApplicationLanguage.ES_ES, label: "Español" },
    { value: ApplicationLanguage.FR_FR, label: "Français" },
    { value: ApplicationLanguage.EL_GR, label: "Ελληνικά" },
    { value: ApplicationLanguage.PL_PL, label: "Polski" },
    { value: ApplicationLanguage.JA_JP, label: "日本語" },
    { value: ApplicationLanguage.CS_CZ, label: "Čeština" },
    { value: ApplicationLanguage.HU_HU, label: "Magyar" },
    { value: ApplicationLanguage.SK_SK, label: "Slovenčina" },
    { value: ApplicationLanguage.UK_UA, label: "Українська" },
    { value: ApplicationLanguage.HE_IL, label: "עברית" },
    { value: ApplicationLanguage.AR_001, label: "العربية" },
].sort((a, b) => a.label.localeCompare(b.label));

interface OutreachHeaderProps {
    companyName: string;
    status: string;
}

const OutreachHeader = ({ companyName, status }: OutreachHeaderProps) => {
    const { t, i18n } = useTranslation();
    const [showMenu, setShowMenu] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setShowMenu(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleUiLanguageChange = (langValue: string) => {
        setCookie("language", langValue);
        i18n.changeLanguage(langValue);
        setShowMenu(false);
    };

    return (
        <div className="chat-header">
            <div className="header-avatar-container">
                <div className="avatar agent">H</div>
                <div className={`status-dot-mini ${status}`}></div>
            </div>
            <div className="chat-header-info">
                <Heading level="h3" className="company-name">
                    {companyName}
                </Heading>
                <Text size="small" className="job-role">
                    {t("Outreach.Chat_InterviewTitle", "Job Interview")}
                </Text>
            </div>
            <div className="header-actions" ref={menuRef}>
                <div className="header-logo-container">
                    <img src={HubertLogo} alt="Hubert Logo" className="header-logo" />
                </div>
                <button className="menu-trigger" onClick={() => setShowMenu(!showMenu)}>
                    <MoreVertical size={20} />
                </button>
                {showMenu && (
                    <div className="header-dropdown-menu">
                        <div className="menu-section-title">
                            <Globe size={14} /> {t("Outreach.Chat_SelectUiLanguage", "UI Language")}
                        </div>
                        <div className="language-menu-list">
                            {availableUiLanguages.map((lang) => (
                                <button
                                    key={lang.value}
                                    className={`language-menu-item ${i18n.language === lang.value ? "active" : ""}`}
                                    onClick={() => handleUiLanguageChange(lang.value)}
                                >
                                    {lang.label}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OutreachHeader;
